import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../config/api';
import { useToast } from '../context/ToastContext';
import { FaClock, FaStore } from 'react-icons/fa';
import {
  DAY_LABELS,
  defaultStoreHours,
  formatDayHours,
  normalizeStoreHours,
  normalizeStoreHoursForSave,
  normalizeTimeInput,
  isValidTimeInput,
  splitTimeInput,
  joinTimeInput,
  normalizeHourPart,
  normalizeMinutePart,
  isValidHourPart,
  isValidMinutePart,
  HOUR_OPTIONS,
  minuteOptionsIncluding,
  filterNumericOptions,
  type StoreHoursConfig,
} from '../utils/storeHours';

function TimePartCombo({
  value,
  disabled,
  options,
  placeholder,
  fieldId,
  ariaLabel,
  normalize,
  isValid,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  options: string[];
  placeholder: string;
  fieldId: string;
  ariaLabel: string;
  normalize: (value: string) => string;
  isValid: (value: string) => boolean;
  onChange: (next: string) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [filterQuery, setFilterQuery] = useState('');
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const filtered = useMemo(
    () => (filterQuery.trim() ? filterNumericOptions(options, filterQuery) : options),
    [options, filterQuery]
  );

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setFilterQuery('');
  }, []);

  const updateMenuPosition = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPos({
      top: rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 56),
    });
  }, []);

  const openMenu = useCallback(
    (query = '') => {
      if (disabled) return;
      setFilterQuery(query);
      updateMenuPosition();
      setOpen(true);
    },
    [disabled, updateMenuPosition]
  );

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onDocDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      closeMenu();
    };
    const onReposition = () => updateMenuPosition();
    document.addEventListener('mousedown', onDocDown);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, closeMenu, updateMenuPosition]);

  const commitDraft = () => {
    const next = normalize(draft);
    if (isValid(next)) {
      onChange(next);
      setDraft(next);
    } else {
      setDraft(value);
    }
    closeMenu();
  };

  const pick = (part: string) => {
    onChange(part);
    setDraft(part);
    closeMenu();
  };

  const menu =
    open && !disabled && filtered.length > 0 && typeof document !== 'undefined'
      ? createPortal(
          <ul
            ref={menuRef}
            id={`${fieldId}-list`}
            role="listbox"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              minWidth: menuPos.width,
              zIndex: 9999,
            }}
            className="max-h-52 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg text-sm"
          >
            {filtered.map((part) => (
              <li key={part} role="option" aria-selected={part === value}>
                <button
                  type="button"
                  className="w-full text-center px-3 py-1.5 hover:bg-amber-50"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(part)}
                >
                  {part}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className="relative w-[3.5rem]">
      <input
        id={fieldId}
        type="text"
        inputMode="numeric"
        maxLength={2}
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          setDraft(e.target.value);
          openMenu(e.target.value);
        }}
        onFocus={() => openMenu('')}
        onBlur={() => window.setTimeout(commitDraft, 120)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitDraft();
          }
          if (e.key === 'Escape') {
            setDraft(value);
            closeMenu();
          }
        }}
        className="w-full rounded border border-gray-300 px-1.5 py-1 pr-5 text-sm text-center disabled:bg-gray-100"
        title={`${ariaLabel}: type or pick from list`}
        autoComplete="off"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={`${fieldId}-list`}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => (open ? closeMenu() : openMenu(''))}
        className="absolute right-0.5 top-1/2 -translate-y-1/2 text-gray-500 text-[10px] px-0.5 disabled:opacity-40"
        aria-label={`${ariaLabel} options`}
      >
        ▾
      </button>
      {menu}
    </div>
  );
}

function StoreHoursTimeField({
  value,
  disabled,
  onChange,
  fieldId,
}: {
  value: string;
  disabled?: boolean;
  onChange: (next: string) => void;
  fieldId: string;
}) {
  const { hour, minute } = splitTimeInput(isValidTimeInput(value) ? value : '08:00');
  const minuteOptions = useMemo(() => minuteOptionsIncluding(minute), [minute]);

  const commitTime = (nextHour: string, nextMinute: string) => {
    if (isValidHourPart(nextHour) && isValidMinutePart(nextMinute)) {
      onChange(joinTimeInput(nextHour, nextMinute));
    }
  };

  return (
    <div className="flex items-center gap-1 flex-nowrap">
      <TimePartCombo
        fieldId={`${fieldId}-hour`}
        ariaLabel="Hour"
        placeholder="08"
        value={hour}
        disabled={disabled}
        options={HOUR_OPTIONS}
        normalize={normalizeHourPart}
        isValid={isValidHourPart}
        onChange={(nextHour) => commitTime(nextHour, minute)}
      />
      <span className="text-gray-500">:</span>
      <TimePartCombo
        fieldId={`${fieldId}-minute`}
        ariaLabel="Minute"
        placeholder="00"
        value={minute}
        disabled={disabled}
        options={minuteOptions}
        normalize={normalizeMinutePart}
        isValid={isValidMinutePart}
        onChange={(nextMinute) => commitTime(hour, nextMinute)}
      />
    </div>
  );
}

export type SettingsSectionId = 'store-hours';

export const SETTINGS_NAV_ITEMS: { id: SettingsSectionId; label: string }[] = [
  { id: 'store-hours', label: 'Store hours & availability' },
];

interface StoreHoursSettingsPanelProps {
  selectedCafe: string;
  cafeName?: string;
  onSaved?: () => void;
}

export default function StoreHoursSettingsPanel({
  selectedCafe,
  cafeName,
  onSaved,
}: StoreHoursSettingsPanelProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<StoreHoursConfig>(defaultStoreHours());
  const allOpenCheckboxRef = useRef<HTMLInputElement>(null);

  const allDaysOpen = config.days.every((d) => !d.closed);
  const someDaysOpen = config.days.some((d) => !d.closed);

  useEffect(() => {
    if (allOpenCheckboxRef.current) {
      allOpenCheckboxRef.current.indeterminate = someDaysOpen && !allDaysOpen;
    }
  }, [someDaysOpen, allDaysOpen]);

  useEffect(() => {
    if (!selectedCafe) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`/api/cafes/${selectedCafe}/store-hours`)
      .then((res) => {
        if (!cancelled) setConfig(normalizeStoreHours(res.data));
      })
      .catch(() => {
        if (!cancelled) setConfig(defaultStoreHours());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCafe]);

  const updateDay = (day: number, patch: Partial<StoreHoursConfig['days'][0]>) => {
    setConfig((prev) => ({
      ...prev,
      days: prev.days.map((row) => (row.day === day ? { ...row, ...patch } : row)),
    }));
  };

  const setAllDaysOpen = (open: boolean) => {
    setConfig((prev) => ({
      ...prev,
      days: prev.days.map((row) => ({ ...row, closed: !open })),
    }));
  };

  const applySundayHoursToAllDays = () => {
    const template = config.days[0];
    if (!template) return;
    setConfig((prev) => ({
      ...prev,
      days: prev.days.map((row) => ({
        ...row,
        open: template.open,
        close: template.close,
      })),
    }));
    showToast(`Applied ${DAY_LABELS[template.day]} hours (${template.open}–${template.close}) to all days`, 'success');
  };

  const save = async () => {
    if (!selectedCafe) {
      showToast('Select a café under Location (not "All cafés") before saving hours.', 'error');
      return;
    }
    const invalidDay = config.days.find(
      (row) => !row.closed && (!isValidTimeInput(row.open) || !isValidTimeInput(row.close))
    );
    if (invalidDay) {
      showToast(
        `${DAY_LABELS[invalidDay.day]}: use HH:MM format (e.g. 08:00) for open and close times.`,
        'error'
      );
      return;
    }

    setSaving(true);
    try {
      const payload = normalizeStoreHoursForSave(config);
      const { data } = await axios.patch(`/api/cafes/${selectedCafe}/store-hours`, payload);
      setConfig(normalizeStoreHours(data));
      onSaved?.();
      showToast('Store hours saved', 'success');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to save store hours';
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedCafe) {
    return (
      <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <p className="text-sm text-gray-600">
          Choose a specific café under <span className="font-semibold">Location</span> in the sidebar
          (not <span className="font-semibold">All cafés</span>) to set opening hours and order
          availability.
        </p>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100 space-y-6">
      <div>
        <h2 className="text-xl font-bold text-amber-900 flex items-center gap-2">
          <FaClock className="text-amber-700" />
          Store hours &amp; availability
        </h2>
        <p className="text-sm text-gray-600 mt-2 max-w-3xl">
          Set when {cafeName || 'this café'} is open for orders.{' '}
          <span className="font-semibold">Performance → Online rate</span> compares these scheduled
          days to days with completed orders (Jordan time).
        </p>
        <p className="text-sm text-amber-800 mt-2 max-w-3xl">
          For each day, leave <span className="font-semibold">Open</span> checked to set hours.
          Uncheck it only for days the café is fully closed.
        </p>
      </div>

      {!loading && config.days.every((d) => d.closed) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Every day is marked closed, so customers cannot order on any day. Check{' '}
          <span className="font-semibold">Open</span> for the days you operate, or use{' '}
          <span className="font-semibold">Standard week</span> below.
        </div>
      )}

      <label className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 cursor-pointer">
        <input
          type="checkbox"
          checked={config.accepting_orders}
          onChange={(e) => setConfig((prev) => ({ ...prev, accepting_orders: e.target.checked }))}
          className="mt-1 h-4 w-4 accent-emerald-700"
        />
        <span>
          <span className="font-semibold text-gray-900 flex items-center gap-2">
            <FaStore className="text-emerald-700" />
            Accepting orders now
          </span>
          <span className="block text-xs text-gray-600 mt-1">
            Turn off when closed unexpectedly (holiday, outage). Hours below are your regular weekly
            schedule.
          </span>
        </span>
      </label>

      {loading ? (
        <p className="text-sm text-gray-500">Loading hours…</p>
      ) : (
        <>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() =>
              setConfig((prev) => ({
                ...prev,
                accepting_orders: true,
                days: prev.days.map((row) => ({
                  ...row,
                  closed: false,
                  open: '08:00',
                  close: '22:00',
                })),
              }))
            }
            className="px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs font-semibold hover:bg-amber-100"
          >
            Standard week (8:00–22:00, all days open)
          </button>
          <button
            type="button"
            onClick={applySundayHoursToAllDays}
            className="px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs font-semibold hover:bg-amber-100"
          >
            Apply Sunday hours to all days
          </button>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-amber-50 text-left text-xs uppercase tracking-wide text-amber-900">
              <tr>
                <th className="px-4 py-3">Day</th>
                <th className="px-4 py-3">
                  <label className="inline-flex flex-col items-start gap-1 cursor-pointer normal-case tracking-normal">
                    <span>Open</span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800">
                      <input
                        ref={allOpenCheckboxRef}
                        type="checkbox"
                        checked={allDaysOpen}
                        onChange={(e) => setAllDaysOpen(e.target.checked)}
                        className="h-3.5 w-3.5 accent-amber-700"
                        aria-label="Select all days as open"
                      />
                      All
                    </span>
                  </label>
                </th>
                <th className="px-4 py-3">Opens</th>
                <th className="px-4 py-3">Closes</th>
                <th className="px-4 py-3 hidden sm:table-cell">Summary</th>
              </tr>
            </thead>
            <tbody>
              {config.days.map((row) => (
                <tr key={row.day} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-medium text-gray-900">{DAY_LABELS[row.day]}</td>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={!row.closed}
                      onChange={(e) => updateDay(row.day, { closed: !e.target.checked })}
                      className="h-4 w-4 accent-amber-700"
                      aria-label={`${DAY_LABELS[row.day]} open for orders`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <StoreHoursTimeField
                      fieldId={`store-hours-${selectedCafe}-${row.day}-open`}
                      value={row.open}
                      disabled={row.closed}
                      onChange={(open) => updateDay(row.day, { open })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <StoreHoursTimeField
                      fieldId={`store-hours-${selectedCafe}-${row.day}-close`}
                      value={row.close}
                      disabled={row.closed}
                      onChange={(close) => updateDay(row.day, { close })}
                    />
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">
                    {formatDayHours(row)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving || loading}
          className="px-5 py-2.5 rounded-lg bg-amber-700 text-white text-sm font-semibold hover:bg-amber-800 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save hours'}
        </button>
      </div>
    </section>
  );
}
